import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

/**
 * Persistent banner shown across the top of the app whenever the current
 * session is acting as another user (SuperAdmin impersonation).
 *
 * Renders nothing when not impersonating. Uses high-contrast red intentionally
 * so the actor can never forget they're acting as someone else.
 */
export default function ImpersonationBanner() {
  const { impersonatedAs, endImpersonation } = useAuth();
  const [remaining, setRemaining] = useState('');

  // Live countdown to expiry. Updates once per second while a session is active.
  useEffect(() => {
    if (!impersonatedAs?.expiresAt) return;
    const tick = () => {
      const ms = new Date(impersonatedAs.expiresAt).getTime() - Date.now();
      if (ms <= 0) { setRemaining('expired'); return; }
      const totalSec = Math.floor(ms / 1000);
      const m = Math.floor(totalSec / 60);
      const s = totalSec % 60;
      setRemaining(`${m}m ${String(s).padStart(2, '0')}s`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [impersonatedAs?.expiresAt]);

  if (!impersonatedAs) return null;

  const target = impersonatedAs.target || {};
  const actor  = impersonatedAs.actor  || {};

  return (
    <div
      role="alert"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        background: 'linear-gradient(90deg, #b91c1c, #dc2626)',
        color: '#fff',
        padding: '8px 16px',
        fontSize: 13,
        fontWeight: 500,
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 12,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      }}
    >
      <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
        <path d="M12 9v2m0 4h.01M4.93 19h14.14c1.54 0 2.5-1.67 1.73-3L13.73 4a2 2 0 00-3.46 0L3.2 16c-.77 1.33.19 3 1.73 3z" />
      </svg>

      <span style={{ minWidth: 0, flex: '1 1 auto', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
        Impersonating <strong>{target.name || 'user'}</strong>
        {target.role && <span style={{ opacity: 0.85 }}> ({target.role})</span>}
        {actor.name && <span style={{ opacity: 0.85 }}> · as {actor.name}</span>}
      </span>

      {remaining && (
        <span style={{ opacity: 0.85, fontVariantNumeric: 'tabular-nums' }}>
          Expires in {remaining}
        </span>
      )}

      <button
        type="button"
        onClick={endImpersonation}
        style={{
          background: 'rgba(255,255,255,0.18)',
          border: '1px solid rgba(255,255,255,0.35)',
          color: '#fff',
          padding: '4px 12px',
          borderRadius: 6,
          fontSize: 12,
          fontWeight: 600,
          cursor: 'pointer',
          marginLeft: 'auto',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        Exit impersonation
      </button>
    </div>
  );
}
